import type { FilterState, SavedSearch } from '../types';
import type { StateSlug } from './stateCore.mjs';
import { buildDirectoryUrl, buildStyleUrl, toDisplaySuburb } from './slugs';
import { DEFAULT_STATE, stateSlugToDisplay, toLocationSlug, getSuburbPostcode } from './stateCore.mjs';

/**
 * The shared builder for filter-carrying search-navigation URLs.
 *
 * Extracted 2026-08-07 (promotables gate, changelog (+11)) from two
 * character-identical implementations that had to be kept in lockstep by a
 * one-sided parity test: App.handleLandingSearch (state-LESS surface,
 * passes DEFAULT_STATE as an explicit product choice — the UI-01 ruling)
 * and LandingPage.navigateToStateSearch (passes the picked entry's state —
 * the UI-28 design). Both now call this function; drift between them is
 * structurally impossible rather than merely test-caught.
 *
 * ✅ THE KNOWN SIBLING IS NOW CONSOLIDATED (item 3, operator-ruled 2026-08-14).
 * This docblock used to record `pages/SavedSearchesPage.handleLoadSearch` as a
 * deliberate non-consolidation, because routing it through here would change its
 * URL bytes — "a separate decision, not a silent ride-along". That was the right
 * call, and the decision has now been taken explicitly rather than silently.
 *
 * What it was hiding: DirectoryPage ALSO loads a saved search, from the same
 * stored fields, and wrote the params in THIS file's order — so one saved search
 * produced two different addresses depending on which page you clicked it from.
 * Both now go through `buildSavedSearchUrl` below, so a single address per saved
 * search is true BY CONSTRUCTION rather than by two call sites agreeing.
 *
 * Verified before ruling: nothing depends on param order — the read side
 * (`useDirectoryFilters`) looks each param up BY NAME, so every previously shared
 * or bookmarked link keeps working in either order; and multi-filter directory
 * URLs are `noindex`, so no ranking is attached to the old byte order.
 *
 * Shape contract (pinned by lib/searchNavigation.test.ts and, end-to-end,
 * by pages/LandingPage.heroSearch.test.tsx's URL-parity cases):
 *   - exactly ONE style  → canonical style PATH (no `styles` query param)
 *   - zero or 2+ styles  → directory path (+ `styles` param when 2+)
 *   - radius only when filters.useRadius; age/exp only when non-empty
 *   - no params → the bare base path (no trailing `?`)
 */
export const buildFilteredSearchUrl = (filters: FilterState, state: StateSlug): string => {
    // Single style → canonical path-based URL; multiple → query param (noindex)
    const basePath = filters.styles.length === 1
        ? buildStyleUrl(filters.suburb, filters.styles[0]!, state) // safe: length === 1 guarantees index 0
        : buildDirectoryUrl(filters.suburb, state);

    const params = new URLSearchParams();
    if (filters.useRadius) params.set('radius', filters.radius.toString());
    if (filters.styles.length > 1) params.set('styles', filters.styles.join(','));
    if (filters.ageGroups.length > 0) params.set('age', filters.ageGroups.join(','));
    if (filters.experienceLevels.length > 0) params.set('exp', filters.experienceLevels.join(','));

    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * The FilterState a stored saved search represents.
 *
 * COVERS the mapping only — it does not read or write the URL. Extracted because
 * DirectoryPage and SavedSearchesPage each built this shape inline, and the two
 * copies disagreed about param ORDER once they reached the URL (item 3).
 *
 * `experienceLevels` tolerates a missing field: documents saved before that filter
 * existed carry no key, and both former call sites defended against it.
 * `state` is NOT part of FilterState — it is the search's own stored state and is
 * applied by `buildSavedSearchUrl` below.
 */
export const filtersFromSavedSearch = (search: SavedSearch): FilterState => ({
    suburb: search.location,
    radius: search.radius,
    useRadius: search.useRadius,
    styles: search.filters.styles,
    ageGroups: search.filters.ageGroups,
    experienceLevels: search.filters.experienceLevels || [],
    gymName: '',
});

/**
 * The ONE address a saved search resolves to, from anywhere in the app.
 *
 * ⚠️ Use this rather than composing `buildFilteredSearchUrl(filtersFromSavedSearch(…))`
 * yourself: the state fallback is part of the contract. UI-01 — a document saved
 * before state stamping carries no `state`, and every such search was always
 * applied against DEFAULT_STATE. Falling back reproduces that exactly rather than
 * guessing, so a null-state search saved from a VIC page keeps applying against
 * NSW until it is re-saved.
 */
/**
 * The ONE location line shown for a saved search, on EVERY surface that lists one.
 *
 * 🪦 SUPERSEDES `savedSearchStateTag` (UI-104, 2026-08-17), removed 2026-08-17 the same
 * day. That helper appended " • NSW" to whatever string the document happened to hold,
 * which read correctly for one storage format and DUPLICATED THE STATE for the other:
 * "Prestons NSW 2170 • NSW". Do not reintroduce a suffix-based tag — the defect was
 * never the tag, it was that `location` is free text written by two different paths.
 *
 * ⭐ OPERATOR-RULED 2026-08-17: every saved search reads "Suburb STATE POSTCODE", built
 * at DISPLAY TIME from what is already stored. Nothing is written to Firestore and no
 * document is migrated — the ruling was explicitly the zero-risk option.
 *
 * WHY A DERIVATION IS NEEDED AT ALL. `SavedSearch.location` is free text and two live
 * call sites disagree on its format, so production holds both at once (measured
 * read-only 2026-08-17, 3 of 3 documents):
 *   - the filter autocomplete stores the resolved form   → "Prestons NSW 2170"
 *   - landing on a suburb URL stores toDisplaySuburb()   → "Adamstown"  (postcode stripped)
 * Reconciling them in the READER is what lets one line serve both without touching data.
 *
 * ⚠️⚠️ THE POSTCODE — NOT THE SLUG — IS THE RESOLUTION TEST, and that is load-bearing.
 * `toLocationSlug` FALLS BACK TO DEFAULT_STATE on input it cannot parse: measured,
 * `toLocationSlug('2170')` and `toLocationSlug('')` both return the literal `'nsw'`.
 * Gating on "did I get a slug?" would therefore render a postcode-only or empty
 * location as "Nsw NSW". A returned postcode proves the slug resolved to a real suburb
 * IN THIS STATE, which is the only signal here that cannot be faked by that fallback.
 *
 * ⚠️ NO STATE ON THE DOCUMENT → THE STORED TEXT IS RETURNED UNCHANGED, and this keeps
 * UI-104's ruling rather than dropping it: a label must not invent a state. It also
 * cannot safely invent a postcode, because the lookup is PER-STATE and suburb names
 * collide across NSW/VIC. All 3 live documents carry a state today; this is the path
 * for a pre-stamping document, not dead code.
 *
 * ⚠️ SCOPE (docs/guard-scope-convention.md):
 * COVERS: turning one saved-search document into its display line — every storage
 *   format that exists in production plus the unresolvable and state-less cases.
 * DOES NOT COVER: what gets WRITTEN. Both save paths still store their own format, so
 *   the divergence this reconciles is still being created. Fixing that is a separate
 *   decision (the operator chose display-time only, deliberately).
 * DOES NOT COVER: the radius suffix (" • 10km"), which each call site appends itself
 *   because only the caller knows whether its surface shows radius.
 * SIBLING: `buildSavedSearchUrl` below decides where a saved search GOES. The two
 *   differ ON PURPOSE — behaviour must pick a state (it falls back to DEFAULT_STATE),
 *   a label must not. A document can be labelled state-less and still navigate to NSW.
 */
export const savedSearchLocationLabel = (search: Pick<SavedSearch, 'location' | 'state'>): string => {
    const stored = (search.location ?? '').trim();

    // Cannot name a state we were not told, and cannot look up a per-state postcode
    // without one. Show exactly what was saved.
    if (!search.state) return stored;

    const stateDisplay = stateSlugToDisplay(search.state);
    const bareSlug = toLocationSlug(stored);
    const postcode = getSuburbPostcode(bareSlug, search.state);

    // A real postcode is the proof the slug resolved — see the DEFAULT_STATE fallback
    // warning above. Only then is it safe to rebuild the name from the slug.
    if (postcode) return `${toDisplaySuburb(bareSlug)} ${stateDisplay} ${postcode}`;

    // Unresolvable (a postcode-only location, a locality absent from the map). Keep the
    // stored text and add the state only if it does not already say it, so an
    // already-qualified string cannot become "… NSW NSW".
    const alreadyNamesState = new RegExp(`\\b${stateDisplay}\\b`, 'i').test(stored);
    return stored && !alreadyNamesState ? `${stored} ${stateDisplay}` : stored;
};

export const buildSavedSearchUrl = (search: SavedSearch): string =>
    buildFilteredSearchUrl(filtersFromSavedSearch(search), search.state ?? DEFAULT_STATE);
